/**
 * QMS audit photos — Firebase Storage path audits/{auditId}/photos/{fileName}
 */

import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Timestamp } from 'firebase/firestore';
import { storage } from '../firebase';

const MAX_TOTAL_BYTES = 8 * 1024 * 1024;
const MAX_PHOTOS = 20;

export const auditPhotoService = {
  async compressImage(file) {
    if (!file || file.size <= 1024 * 1024) {
      return file;
    }
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        try {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          const maxDim = 1920;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Canvas toBlob failed'));
            },
            'image/jpeg',
            0.8
          );
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Image load failed'));
      };
      img.src = url;
    });
  },

  validateTotalSize(existingPhotos, newFileSize) {
    if (!Array.isArray(existingPhotos) || existingPhotos.length >= MAX_PHOTOS) {
      return false;
    }
    const sum = existingPhotos.reduce((acc, p) => acc + (Number(p.size) || 0), 0);
    return sum + (Number(newFileSize) || 0) <= MAX_TOTAL_BYTES;
  },

  async uploadPhoto(auditId, file, uploadedByUserId) {
    if (!auditId || !file) throw new Error('auditId and file are required');
    const blob = await this.compressImage(file);
    const safeName = `${Date.now()}_${(file.name || 'photo').replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const storagePath = `audits/${auditId}/photos/${safeName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, blob, { contentType: blob.type || file.type || 'image/jpeg' });
    const url = await getDownloadURL(storageRef);
    const size = blob.size;
    return {
      url,
      storagePath,
      size,
      uploadedAt: Timestamp.now(),
      uploadedBy: uploadedByUserId || null
    };
  },

  async deletePhoto(storagePath) {
    if (!storagePath) return;
    await deleteObject(ref(storage, storagePath));
  }
};
